import { useState } from 'react';
import type { SmokeDetail, SmokeRouteResult } from '../types/smoke.js';
import { Spinner } from './Spinner.js';
import { SmokeStatusBadge } from './SmokeStatusBadge.js';

interface SmokeVerdictPanelProps {
  detail: SmokeDetail | null;
  loading?: boolean;
  error?: string | null;
}

function formatElapsed(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return '—';
  return `${Math.round(ms / 1000)}s`;
}

function formatIso(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function Screenshot({ src, label, slot }: { src: string | null; label: string; slot: 'before' | 'after' }) {
  const [failed, setFailed] = useState(false);

  if (!src) {
    return (
      <div className="text-xs text-gray-400 italic bg-gray-50 border border-gray-100 rounded p-2 text-center">
        {slot === 'before' ? 'no baseline — before screenshot skipped' : 'screenshot unavailable'}
      </div>
    );
  }

  if (failed) {
    return (
      <div className="text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded p-2 text-center">
        screenshot unavailable
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={label}
      onError={() => setFailed(true)}
      className="max-w-full h-auto rounded border border-gray-100 bg-white"
    />
  );
}

function RouteCard({ route }: { route: SmokeRouteResult }) {
  return (
    <div className="border border-gray-100 rounded-md p-2 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-mono font-medium text-gray-700">{route.route}</span>
        <div className="flex items-center gap-2 text-[10px] text-gray-500">
          <span>HTTP {route.httpStatus}</span>
          <span>·</span>
          <span>{formatElapsed(route.elapsedMs)}</span>
        </div>
      </div>

      {route.assertions.length > 0 && (
        <ul className="space-y-1">
          {route.assertions.map((a) => (
            <li key={a.id} className="flex items-start gap-1.5 text-xs">
              <span className={a.ok ? 'text-green-600' : 'text-red-600'} aria-hidden="true">
                {a.ok ? '✓' : '✕'}
              </span>
              <span className="font-medium text-gray-700">{a.id}</span>
              {a.detail && <span className="text-gray-500">— {a.detail}</span>}
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <p className="text-[10px] text-gray-400">Before</p>
          <Screenshot src={route.screenshots.before} label={`${route.route} before`} slot="before" />
        </div>
        <div className="space-y-1">
          <p className="text-[10px] text-gray-400">After</p>
          <Screenshot src={route.screenshots.after} label={`${route.route} after`} slot="after" />
        </div>
      </div>
    </div>
  );
}

export function SmokeVerdictPanel({ detail, loading, error }: SmokeVerdictPanelProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
        <Spinner size={16} />
        Loading smoke verdict…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-red-600 py-2">
        Could not load smoke verdict: {error}
      </div>
    );
  }

  if (!detail || detail.status === 'not_run') {
    return (
      <div className="text-xs text-gray-500 py-2">
        No smoke verdict recorded.
      </div>
    );
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <SmokeStatusBadge
          status={detail.status}
          summary={{
            routeCount: detail.routesTested.length,
            failureCount: detail.failures.length,
            elapsedMs: detail.elapsedMs ?? null,
          }}
        />
        <span className="text-xs text-gray-500">{formatElapsed(detail.elapsedMs)}</span>
        {detail.previewUrl && (
          <a
            href={detail.previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-indigo-600 hover:text-indigo-700 truncate max-w-[200px]"
          >
            {detail.previewUrl}
          </a>
        )}
      </div>

      <div className="text-[11px] text-gray-500 space-y-0.5">
        {detail.account && <div>Account: {detail.account}</div>}
        {detail.headSha && <div className="font-mono">HEAD: {detail.headSha}</div>}
        {detail.ranAt && <div>Ran: {formatIso(detail.ranAt)}</div>}
      </div>

      {detail.error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded p-2">
          {detail.error}
        </div>
      )}

      {detail.routesTested.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-gray-700">Routes tested</h4>
          {detail.routesTested.map((route, idx) => (
            <RouteCard key={`${route.route}-${idx}`} route={route} />
          ))}
        </div>
      )}

      {detail.failures.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-xs font-semibold text-red-700">Failures</h4>
          <ul className="space-y-1.5">
            {detail.failures.map((f, idx) => (
              <li
                key={`${f.route}-${f.id}-${idx}`}
                className="text-xs border-l-2 border-red-300 pl-2 py-0.5"
              >
                <div className="font-mono text-gray-700">{f.route}</div>
                <div className="font-medium text-red-700">{f.id}</div>
                {f.detail && <div className="text-gray-600 mt-0.5">{f.detail}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {detail.warnings.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-xs font-semibold text-amber-700">Warnings</h4>
          <ul className="list-disc list-inside space-y-0.5">
            {detail.warnings.map((w, idx) => (
              <li key={idx} className="text-xs text-amber-800">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {detail.blockedWrites.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-xs font-semibold text-red-700">Blocked writes</h4>
          <ul className="space-y-1">
            {detail.blockedWrites.map((bw, idx) => (
              <li key={idx} className="text-xs font-mono text-gray-700">
                {bw.method} {bw.route} → {bw.url}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
